import { ReactElement, useCallback } from 'react';

import { AxiosResultDefaultError } from '../../../../../api/request';
import { AuthenticationError } from '../../../../../api/requests/tuitionProcess';
import { createFetchContext } from '../../../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../../../components/info/DisplayError';
import Loading from '../../../../../components/info/Loading';
import { getStudentList } from '../api';
import { StudentListQuery, StudentListResponse } from '../types';

interface BaseLoaderProps {
  children: ReactElement;
}

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  StudentListQuery,
  StudentListResponse,
  AuthenticationError | AxiosResultDefaultError
>();

export function useStudentListLoader() {
  return useFetch();
}

export const StudentListProvider = FetchProvider;
export const StudentListConsumer = FetchConsumer;

const DEFAULT_QUERY: StudentListQuery = { page: 0, items_per_page: 10 };

export default function StudentListLoader({ children }: BaseLoaderProps) {
  const request = useCallback(async (query: StudentListQuery) => {
    return getStudentList(query);
  }, []);

  return (
    <FetchProvider
      request={request}
      defaultQuery={DEFAULT_QUERY}
      fetchImmediately={true}
    >
      <FetchConsumer>
        {({ loading, data, error, refresh }) => {
          if (loading && !data) {
            return <Loading insideCard />;
          } else if (error) {
            return (
              <DisplayError
                insideCard
                textBody={error.code}
                retryAction={refresh}
              />
            );
          } else if (data) {
            return children;
          } else {
            return null;
          }
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
